// src/components/AddMeal.tsx

import { useState, useCallback, useEffect } from "react"
import {
  Autocomplete,
  Box,
  Button, 
  Card,
  CardContent,
  Checkbox,
  FormControlLabel,
  IconButton,
  Input,
  InputLabel,
  MenuItem,
  Paper,  
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material"
import DeleteIcon from "@mui/icons-material/Delete"
import AddIcon from "@mui/icons-material/Add"
import { useDispatch, useSelector } from "react-redux"

import { RootState } from "../../app/store"
import {
  addFoodstuffToMeal,
  deleteMealFoodstuff,
  getLikeFoodstuffs,
  patchMeal,
  saveMeal,
  setLikeFoodstuffs,
  updateMealFoodstuffValue
} from "../../state/meals/mealSlice"
import { MeasurementUnits } from "./MeasurementUnits"
import { Foodstuff } from "../../models/Foodstuff"
import { useTranslation } from "react-i18next"
import { Meal } from "../../models/Meal"

import {
  stringsToLanguageCodes,
  globalLanguageCodeToMeal
} from "../common/LanguageCodeMapping"
import { LanguageCode } from "../common/Language"
import { LanguageSelector } from "../common/LanguageSelector"
import { useNotifications } from "../common/notification/NotificationContext"

export function AddMeal() {
  const dispatch = useDispatch<any>()
  const { t } = useTranslation()
  const { addNotification } = useNotifications()

  const mealState = useSelector((state: RootState) => state.meal)
  const rawUserLanguages = useSelector(
    (state: RootState) => state.user.user.languageCodes || []
  )
  const user = useSelector((state: RootState) => state.user.user)

  const userLanguages: LanguageCode[] = stringsToLanguageCodes(rawUserLanguages)

  const [selectedLanguage, setSelectedLanguage] = useState<LanguageCode>(() => {
    if (userLanguages.length > 0) {
      return userLanguages[0]
    } else {
      return LanguageCode.CS
    }
  })

  // Local state for recipe steps
  const [steps, setSteps] = useState<string[]>([])

  const [publicMealChecked, setPublicMealChecked] = useState(true);

  /**
   * Initialize local steps from mealState.meal.recipeSteps
   */
  useEffect(() => {
    if (mealState.meal.recipeSteps && mealState.meal.recipeSteps.length > 0) {
      setSteps(mealState.meal.recipeSteps)
    } else {
      setSteps([""])
    }
  }, [mealState.meal.recipeSteps])

  /**
   * Update selectedLanguage if userLanguages change and selectedLanguage is not set
   */
  useEffect(() => {
    if (!selectedLanguage && userLanguages.length > 0) {
      setSelectedLanguage(userLanguages[0])
      dispatch(patchMeal({ languageCode: globalLanguageCodeToMeal(userLanguages[0]) }))
    }
  }, [userLanguages, selectedLanguage, dispatch])

  /**
   * Handle language change from LanguageSelector
   */
  const handleLanguageChange = useCallback(
    (lang: LanguageCode) => {
      setSelectedLanguage(lang)
      dispatch(patchMeal({ languageCode: globalLanguageCodeToMeal(lang) }))
    },
    [dispatch]
  )

  /**
   * Update a single foodstuff's "value" in Redux
   */
  const updateFoodstuffValueHandler = (id: string, value: string) => {
    dispatch(updateMealFoodstuffValue({ id, value }))
  }

  const saveMealHandler = () => {
    const filteredSteps = steps.map(step => step.trim()).filter(step => step !== "")

    dispatch(
      patchMeal({
        recipeSteps: filteredSteps,
        languageCode: globalLanguageCodeToMeal(selectedLanguage),
      })
    )

    dispatch(
      saveMeal({
        ...mealState.meal,
        public: mealState.meal.public,
        recipeSteps: filteredSteps,
        languageCode: globalLanguageCodeToMeal(selectedLanguage),
      })
    )
  }

  const saveMealHandlerAsync = async () => {
    try {
      const filteredSteps = steps.map(step => step.trim()).filter(step => step !== "")
      dispatch(
        patchMeal({
          recipeSteps: filteredSteps,
          languageCode: globalLanguageCodeToMeal(selectedLanguage),
        })
      );

      await dispatch(
        saveMeal({
          ...mealState.meal,
          public: mealState.meal.public,
          recipeSteps: filteredSteps,
          languageCode: globalLanguageCodeToMeal(selectedLanguage),
        })
      ).unwrap();
      addNotification(
        "Meal saved successfully!",
        'info'
      );
    } catch (err: any) {
      addNotification(
        err.message ?? "Failed to save meal",
        "warning"
      );
    }
  };

  const updateMealHandler = (type: string, value: string) => {
    if (type === "NAME") {
      dispatch(patchMeal({ name: value || "" }))
    }
  }

  const deleteMealFoodstuffHandler = (foodstuffId: string) => {
    dispatch(deleteMealFoodstuff(foodstuffId))
  }

  const calculateBallancablaValue = (type: string, fs: Foodstuff): number => {
    switch (type) {
      case "ENERGY":
        return (fs.energyInKcal || 0) * (fs.weight ?? 0)
      case "PROTEIN":
        return (fs.proteinMassFraction || 0) * (fs.weight ?? 0)
      case "CARBOHYDRATE":
        return (fs.carbohydrateMassFraction || 0) * (fs.weight ?? 0)
      case "FAT":
        return (fs.fatMassFraction || 0) * (fs.weight ?? 0)
      case "FIBRE":
        return (fs.fibreMassFraction || 0) * (fs.weight ?? 0)
      default:
        return 0
    }
  }

  const calculateTotalBallancableValue = (type: string): number => {
    return (
      mealState.meal.foodstuffs?.reduce(
        (acc, fs) => acc + calculateBallancablaValue(type, fs),
        0
      ) || 0
    )
  }

  const calculateTotalWeight = (): number => {
    return (
      mealState.meal.foodstuffs?.reduce((acc, fs) => acc + (fs.weight ?? 0), 0) || 0
    )
  }

  const isSaveButtonDisabled = (m: Meal) => {
    const isFoodstuffValid = (m.foodstuffs?.length || 0) > 1
    const isNameValid = Boolean(m.name?.trim())
    return !(isFoodstuffValid && isNameValid)
  }

  const handleStepChange = (index: number, value: string) => {
    const updatedSteps = [...steps]
    updatedSteps[index] = value
    setSteps(updatedSteps)
  }

  const handleDeleteStep = (index: number) => {
    if (steps.length === 1) return
    const updatedSteps = steps.filter((_, i) => i !== index)
    setSteps(updatedSteps)
  }

  const handleAddStep = () => {
    setSteps([...steps, ""])
  }

  const handleIsPublicChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = event.target.checked;
    setPublicMealChecked(newValue);
    dispatch(patchMeal({ public: newValue }));
  };

  return (
    <Card sx={{ minWidth: 275, margin: 5, padding: 3, boxShadow: 3 }}>
      <CardContent>
        <Typography variant="h5" align="center" gutterBottom>
          {t("addMeal_addMeal")}
        </Typography>

        <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
          <TextField
            margin="normal"
            id="name"
            label={t("common_name")}
            name={t("addMeal_addMeal")}
            value={mealState.meal.name || ""}
            onChange={(e) => updateMealHandler("NAME", e.target.value)}
          />

          {/* Steps */}
          <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
            {steps.map((step, index) => (
              <Box
                key={index}
                sx={{ display: "flex", alignItems: "center", gap: 1 }}
              >
                <TextField
                  multiline
                  margin="normal"
                  label={`${t("addMeal_receipt")} #${index + 1}`}
                  value={step}
                  onChange={(e) => handleStepChange(index, e.target.value)}
                  sx={{
                    flexGrow: 1,
                    "& .MuiInputBase-root": { minHeight: "80px", padding: "12px" },
                    "& textarea": { lineHeight: "1.6", fontSize: "16px" },
                  }}
                />
                <IconButton
                  aria-label="delete-step"
                  onClick={() => handleDeleteStep(index)}
                  disabled={steps.length === 1} // Disable if only one step remains
                  color="error"
                  sx={{ marginTop: "8px" }}
                >
                  <DeleteIcon />
                </IconButton>
              </Box>
            ))}

            <Button
              variant="outlined"
              startIcon={<AddIcon />}
              onClick={handleAddStep}
              sx={{ alignSelf: "flex-start", mt: 1 }}
            >
              {t("add_step")}
            </Button>
          </Box>
        </Box>

        <LanguageSelector
          languages={userLanguages}
          selectedLanguage={selectedLanguage}
          onLanguageChange={handleLanguageChange}
        />

        <InputLabel id="demo-simple-select-label">{t("addMeal_mealtime")}</InputLabel>
        <Select
          labelId="demo-simple-select-label"
          id="demo-simple-select"
          value={mealState.meal.mealtime}
          onChange={(e) => dispatch(patchMeal({ mealtime: e.target.value }))}
          defaultValue={"BREAKFAST"}
          sx={{ mb: 2 }}
        >
          <MenuItem value={"BREAKFAST"}>{t("addMeal_breakfast")}</MenuItem>
          <MenuItem value={"SNACK_AM"}>{t("addMeal_snackAM")}</MenuItem>
          <MenuItem value={"LUNCH"}>{t("addMeal_lunch")}</MenuItem>
          <MenuItem value={"SNACK_PM"}>{t("addMeal_snackPM")}</MenuItem>
          <MenuItem value={"DINNER"}>{t("addMeal_dinner")}</MenuItem>
        </Select>

        <Autocomplete
          disablePortal
          id="combo-box-demo"
          options={mealState.foodstuff.likeFoodstuffs}
          getOptionLabel={(option) => option.name || ""}
          onChange={(event: any, value: Foodstuff | null) => {
            if (value) {
              dispatch(addFoodstuffToMeal(value))
            }
          }}
          sx={{ width: "100%", minWidth: "300px", padding: "10px" }}
          renderInput={(params) => (
            <TextField
              {...params}
              label={t("common_foodstuff")}
              onChange={(e) => {
                const textValue = e.target.value
                if (!textValue) {
                  dispatch(setLikeFoodstuffs([]))
                } else if (textValue.length > 2) {
                  dispatch(getLikeFoodstuffs(textValue))
                }
              }}
            />
          )}
        />

        {(mealState.meal.foodstuffs?.length || 0) > 0 && (
          <TableContainer component={Paper} sx={{ mt: 3 }}>
            <Table aria-label="simple table">
              <TableHead>
                <TableRow>
                  <TableCell align="center">{t("common_name")}</TableCell>
                  <TableCell align="center">{t("common_actions")}</TableCell>
                  <TableCell align="center">{t("common_value")}</TableCell>
                  <TableCell align="center">{t("common_unit")}</TableCell>
                  <TableCell align="center" sx={{ fontWeight: "bold" }}>
                    {t("common_weight")} [g]
                  </TableCell>
                  <TableCell align="center" sx={{ fontWeight: "bold" }}>
                    {t("common_energy")} [kcal]
                  </TableCell>
                  <TableCell align="center" sx={{ fontWeight: "bold" }}>
                    {t("common_fat")} [g]
                  </TableCell>
                  <TableCell align="center" sx={{ fontWeight: "bold" }}>
                    {t("common_carbohydrate")} [g]
                  </TableCell>
                  <TableCell align="center" sx={{ fontWeight: "bold" }}>
                    {t("common_protein")} [g]
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {mealState.meal.foodstuffs?.map((fs, i) => (
                  <TableRow
                    key={i}
                    sx={{
                      "&:last-child td, &:last-child th": { border: 0 },
                      "&:hover": { backgroundColor: "#F3EEE8", cursor: "pointer" },
                    }}
                  >
                    <TableCell align="center">{fs.name}</TableCell>
                    <TableCell align="center">
                      <IconButton
                        onClick={() => deleteMealFoodstuffHandler(fs.id!)}
                        color="error"
                      >
                        <DeleteIcon />
                      </IconButton>
                    </TableCell>
                    <TableCell align="center">
                      <Input
                        value={fs.value || ""}
                        onChange={(e) =>
                          updateFoodstuffValueHandler(fs.id!, e.target.value)
                        }
                        type="number"
                        inputProps={{ min: 0 }}
                      />
                    </TableCell>
                    <TableCell align="center">
                      {fs.measurementUnits && fs.measurementUnits.length > 1 ? (
                        <MeasurementUnits foodstuff={fs} />
                      ) : (
                        <Typography>g</Typography>
                      )}
                    </TableCell>
                    <TableCell align="center">{(fs.weight || 0).toFixed(0)}</TableCell>
                    <TableCell align="center">
                      {calculateBallancablaValue("ENERGY", fs).toFixed(0)}
                    </TableCell>
                    <TableCell align="center">
                      {calculateBallancablaValue("FAT", fs).toFixed(0)}
                    </TableCell>
                    <TableCell align="center">
                      {calculateBallancablaValue("CARBOHYDRATE", fs).toFixed(0)}
                    </TableCell>
                    <TableCell align="center">
                      {calculateBallancablaValue("PROTEIN", fs).toFixed(0)}
                    </TableCell>
                  </TableRow>
                ))}

                {mealState.meal.foodstuffs!!.length > 1 && (
                  <TableRow>
                    <TableCell align="center">{t("common_total")}</TableCell>
                    <TableCell />
                    <TableCell />
                    <TableCell />
                    <TableCell align="center">
                      {calculateTotalWeight().toFixed(0)}
                    </TableCell>
                    <TableCell align="center">
                      {calculateTotalBallancableValue("ENERGY").toFixed(0)}
                    </TableCell>
                    <TableCell align="center">
                      {calculateTotalBallancableValue("FAT").toFixed(0)}
                    </TableCell>
                    <TableCell align="center">
                      {calculateTotalBallancableValue("CARBOHYDRATE").toFixed(0)}
                    </TableCell>
                    <TableCell align="center">
                      {calculateTotalBallancableValue("PROTEIN").toFixed(0)}
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        )}

        {user.premium &&
          <FormControlLabel
            control={
              <Checkbox
                checked={!!mealState.meal.public}
                onChange={(e) => {
                  dispatch(patchMeal({ public: e.target.checked }))
                }}
                name="myCheckbox"
                color="primary"
              />
            }
            label="Save as public meal"
          />
        }

        <Button
          variant="contained"
          color="success"
          onClick={saveMealHandlerAsync}
          disabled={isSaveButtonDisabled(mealState.meal)}
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            margin: "20px auto",
            padding: "12px 40px",
            borderRadius: "20px",
            backgroundColor: "#28a745",
            ":hover": { backgroundColor: "#218838" },
          }}
        >
          {t("common_save")}
        </Button>
      </CardContent>
    </Card>
  )
}
